/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';

/* Components */
import LanderPage from '../../../components/LanderPage';
import LanderLogo from '../../../components/LanderLogo';

/* Providers */
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Utils */
import useQuery from '../../../hooks/useQuery';
import { sizeByViewport } from '../../../utils/cardsUtils';
import { useCountry } from '../../../providers/ConfigProvider';
import {
  formatPlaySource,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
} from '../../../utils/googleTags';

/* Styles */
import './Lander.scss';

const LOGO_HEIGHT = sizeByViewport(164);

function Lander({ sourceProvider, children }) {
  const { state: locationState } = useLocation();
  const { pageId } = useParams();
  const { landerName } = useQuery();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideLogo, setHideLogo] = useState(false);
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const country = useCountry();

  const handleScroll = useCallback((scrollOffset, maxScrollOffset) => {
    setIsScrolled(!!scrollOffset);
    setHideLogo(
      !!children
      && maxScrollOffset > LOGO_HEIGHT
      && scrollOffset + LOGO_HEIGHT >= maxScrollOffset,
    );
  }, [children]);

  const landerLogo = useMemo(() => (
    <LanderLogo
      className="dtv-html5-c-lander__logo"
      landerName={landerName}
      small={isScrolled}
      hidden={hideLogo}
    />
  ), [hideLogo, isScrolled, landerName]);

  useEffect(() => {
    if (tagsMountRef.current && country && pageId && locationState) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.NETWORK_AND_COLLECTION_PAGE_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.NETWORK_PAGE,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
        data: {
          providerName: pageId,
          content: {
            playSource: formatPlaySource({
              parentLander: locationState?.parentLander,
              section: locationState?.carouselTitle,
              provider: locationState?.providerName,
            }) || locationState?.gtmPlaySource,
          },
        },
      });
      tagsMountRef.current = false;
    }
  }, [
    commonTaggingEvent,
    country,
    locationState,
    locationState?.carouselTitle,
    locationState?.gtmPlaySource,
    locationState?.parentLander,
    locationState?.providerName,
    pageId,
  ]);

  return (
    <LanderPage
      pageId={pageId}
      sourceProvider={sourceProvider}
      preContent={landerLogo}
      onScroll={handleScroll}
    >
      {children}
    </LanderPage>
  );
}

Lander.propTypes = {
  sourceProvider: PropTypes.string,
  children: PropTypes.node,
};

Lander.defaultProps = {
  sourceProvider: '',
  children: undefined,
};

export default memo(Lander);
